/**
 * Notification API client for the admin portal alerts page and toast pop-ups.
 */

import { apiClient, transformKeys } from './api-client';
import type {
  InAppNotification,
  NotificationStats,
  NotificationListFilters,
} from '@/types/notification';

export interface NotificationListResponse {
  list: InAppNotification[];
  total: number;
  hasMore: boolean;
}

export async function getNotifications(
  filters: NotificationListFilters = {},
): Promise<NotificationListResponse> {
  const params: Record<string, unknown> = {};
  if (filters.category) params.category = filters.category;
  if (filters.severity) params.severity = filters.severity;
  if (filters.isRead !== undefined) params.is_read = String(filters.isRead);
  params.limit = filters.limit ?? 50;
  params.offset = filters.offset ?? 0;

  const { data } = await apiClient.get('/notifications', { params });
  const items = (data.data ?? []).map((n: unknown) => transformKeys<InAppNotification>(n));
  const pagination = data.pagination ?? {};

  return {
    list: items,
    total: pagination.total_count ?? items.length,
    hasMore: pagination.has_more ?? false,
  };
}

export async function getNotificationStats(): Promise<NotificationStats> {
  const { data } = await apiClient.get('/notifications/stats');
  return transformKeys<NotificationStats>(data);
}

export async function getRecentNotifications(): Promise<InAppNotification[]> {
  const { data } = await apiClient.get('/notifications/recent');
  return (data.data ?? []).map((n: unknown) => transformKeys<InAppNotification>(n));
}

export async function markAsRead(id: string): Promise<void> {
  await apiClient.patch(`/notifications/${id}/read`);
}

export async function markAllAsRead(): Promise<void> {
  await apiClient.patch('/notifications/read-all');
}

export async function dismissNotification(id: string): Promise<void> {
  await apiClient.delete(`/notifications/${id}`);
}

export async function dismissAllNotifications(): Promise<void> {
  await apiClient.delete('/notifications/dismiss-all');
}
